import { useSyncExternalStore } from 'react';

/**
 * State shared between the Procurement breadcrumb bars (Header) and the
 * Procurement screens: the Archived switch and Filters popover of each list,
 * and the open record's name and archive state on a detail page. A tiny
 * external store, so neither App nor Header needs new props for it.
 */
/** The two ways the Shipments page can be viewed: the full table, or only the shipments with goods waiting to be received. */
export type ShipmentsPageView = 'table' | 'ready';

export interface ProcurementBarState {
  orders: { archived: boolean; status: string; payment: string; receiving: string };
  shipments: { status: string; leg: string; mode: string; view: ShipmentsPageView; /** Shipments with goods waiting to be received, published by the page for the view switch. */ readyCount: number };
  agents: { showInactive: boolean };
  inventory: { archived: boolean; itemType: string; /** Item types found on the loaded items (nothing is hard-coded). */ itemTypes: string[] };
  /** The record open on a detail page (null on lists and while loading). */
  detail: { kind: 'order' | 'shipment' | 'agent'; id: string; title: string; archived: boolean; status?: string } | null;
}

const INITIAL: ProcurementBarState = {
  orders: { archived: false, status: '', payment: '', receiving: '' },
  shipments: { status: '', leg: '', mode: '', view: 'table', readyCount: 0 },
  agents: { showInactive: false },
  inventory: { archived: false, itemType: '', itemTypes: [] },
  detail: null,
};

let state: ProcurementBarState = INITIAL;
const listeners = new Set<() => void>();
const emit = () => listeners.forEach(l => l());

export const getProcurementBar = (): ProcurementBarState => state;

type Section = 'orders' | 'shipments' | 'agents' | 'inventory';

const sameValue = (a: unknown, b: unknown): boolean =>
  Object.is(a, b) || (Array.isArray(a) && Array.isArray(b) && a.length === b.length && a.every((v, i) => Object.is(v, b[i])));

/**
 * A patch that changes nothing is dropped, so a screen that publishes into the bar from an effect can never
 * re-trigger itself (a new but equal array used to cause an endless render loop, React error 185).
 */
function patchSection<K extends Section>(key: K, patch: Partial<ProcurementBarState[K]>): void {
  const current = state[key] as Record<string, unknown>;
  const changed = Object.entries(patch).some(([k, v]) => !sameValue(current[k], v));
  if (!changed) return;
  state = { ...state, [key]: { ...state[key], ...patch } };
  emit();
}

export const setOrdersBar = (patch: Partial<ProcurementBarState['orders']>) => patchSection('orders', patch);
export const setShipmentsBar = (patch: Partial<ProcurementBarState['shipments']>) => patchSection('shipments', patch);
export const setAgentsBar = (patch: Partial<ProcurementBarState['agents']>) => patchSection('agents', patch);
export const setInventoryBar = (patch: Partial<ProcurementBarState['inventory']>) => patchSection('inventory', patch);

export function setBarDetail(detail: ProcurementBarState['detail']): void {
  state = { ...state, detail };
  emit();
}

export function resetProcurementBar(): void {
  state = INITIAL;
  emit();
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => { listeners.delete(listener); };
}

export function useProcurementBar(): ProcurementBarState {
  return useSyncExternalStore(subscribe, getProcurementBar, getProcurementBar);
}

/** Events the detail bars send to the open detail page (the page owns its form). */
export const PROC_EVENTS = {
  orderSave: 'procurement-order:save',
  orderArchive: 'procurement-order:archive',
  orderRestore: 'procurement-order:restore',
  orderReceive: 'procurement-order:receive',
  shipmentSave: 'procurement-shipment:save',
  shipmentActivate: 'procurement-shipment:activate',
  shipmentCancel: 'procurement-shipment:cancel',
  shipmentReactivate: 'procurement-shipment:reactivate',
  inventoryAdd: 'procurement-inventory:add',
} as const;
